'use client';

import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useSession } from '@/lib/session';
import {
  type CrmFlowChange,
  type CrmFlowLink,
  type CrmFlowRun,
  type CrmFlowStep,
  type CrmFlowStepRecord,
  crmFlowStepChecks,
  crmFlowStepLabels,
  crmFlowStepValues,
} from '@borradh-workspace/api-client/types';
import { ExternalLink, Play, RotateCcw, Trash2 } from 'lucide-react';
import { useId, useState } from 'react';
import {
  useCleanUpCrmFlow,
  useCrmFlowRun,
  useCrmFlowRuns,
  useRunCrmFlowStep,
  useStartCrmFlow,
} from '../../api/crm-triggers';

/** What a failed request said, for the card: the API's own message. */
const failure = (error: unknown) => {
  const status = (error as { status?: unknown } | null)?.status;
  const message = error instanceof Error ? error.message : String(error);
  return typeof status === 'number' ? `${status}: ${message}` : message;
};

const time = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', {
    dateStyle: 'short',
    timeStyle: 'short',
  });

function RecordLinks({ links }: { links: CrmFlowLink[] }) {
  if (!links.length) return null;
  return (
    <ul className="flex flex-wrap gap-2">
      {links.map((link) => (
        <li key={link.url}>
          <Button asChild variant="outline" size="sm">
            <a href={link.url} target="_blank" rel="noreferrer">
              <ExternalLink />
              <span className="capitalize">{link.kind}:</span> {link.title}
            </a>
          </Button>
        </li>
      ))}
    </ul>
  );
}

function ChangesTable({
  changes,
  label,
}: {
  changes: CrmFlowChange[];
  label: string;
}) {
  if (!changes.length)
    return (
      <p className="text-xs text-muted-foreground">
        No field changed in Twenty.
      </p>
    );
  return (
    <Table aria-label={label}>
      <TableHeader>
        <TableRow>
          <TableHead>Record</TableHead>
          <TableHead>Field</TableHead>
          <TableHead>Before</TableHead>
          <TableHead>After</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {changes.map((c) => (
          <TableRow key={`${c.record}.${c.field}`}>
            <TableCell>
              <a
                href={c.url}
                target="_blank"
                rel="noreferrer"
                className="capitalize underline"
              >
                {c.record}
              </a>
            </TableCell>
            <TableCell className="font-mono text-xs">{c.field}</TableCell>
            <TableCell className="max-w-48 truncate text-xs text-muted-foreground">
              {c.before || '—'}
            </TableCell>
            <TableCell className="max-w-48 truncate text-xs">
              {c.after || '—'}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** A step that has run: what it did, what changed, and what to check in Twenty. */
function DoneStep({ record }: { record: CrmFlowStepRecord }) {
  const label = crmFlowStepLabels[record.step];
  return (
    <div className="flex flex-col gap-3 text-sm">
      <p>{record.summary}</p>
      <p className="rounded-md bg-muted p-2 text-xs">
        <span className="font-medium">Check in Twenty: </span>
        {crmFlowStepChecks[record.step]}
      </p>
      <ChangesTable changes={record.changes} label={`${label} changes`} />
      {record.created.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium">Added in Twenty</span>
          <RecordLinks links={record.created} />
        </div>
      )}
      {record.messages.length > 0 && (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium">
            Slack messages (sent to you)
          </span>
          <ul className="flex flex-col gap-1 text-xs">
            {record.messages.map((m) => (
              <li key={`${m.at}.${m.meantFor}.${m.text}`}>
                <Badge variant={m.delivered ? 'secondary' : 'outline'}>
                  {m.delivered ? 'Sent' : 'Not delivered'}
                </Badge>{' '}
                <span className="text-muted-foreground">
                  meant for {m.meantFor}:
                </span>{' '}
                {m.text}
              </li>
            ))}
          </ul>
        </div>
      )}
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">
          The CRM code’s answer · {time(record.at)} by {record.by}
        </summary>
        <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-muted p-3">
          {JSON.stringify(record.result, null, 2)}
        </pre>
      </details>
    </div>
  );
}

/** The ordered steps: done ones expanded, only the next one runnable. */
function StepList({ run }: { run: CrmFlowRun }) {
  const { runStep, isRunningStep } = useRunCrmFlowStep();
  const [error, setError] = useState<string | null>(null);
  const records = new Map(run.history.map((r) => [r.step, r]));

  const onRun = async (step: CrmFlowStep, mode?: 'simulate' | 'real') => {
    setError(null);
    try {
      await runStep({ runId: run.runId, step, ...(mode ? { mode } : {}) });
    } catch (e) {
      setError(failure(e));
    }
  };

  return (
    <ol className="flex flex-col gap-2">
      {crmFlowStepValues.map((step, i) => {
        const record = records.get(step);
        const isNext = run.next === step && !run.cleanedUp;
        return (
          <li
            key={step}
            aria-label={crmFlowStepLabels[step]}
            className={
              isNext
                ? 'rounded-md border border-primary p-3'
                : 'rounded-md border p-3'
            }
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium">
                {i + 1}. {crmFlowStepLabels[step]}
              </span>
              {record ? (
                <Badge variant="secondary">Done</Badge>
              ) : isNext ? (
                <div className="flex gap-2">
                  {step === 'sign' ? (
                    <>
                      <Button
                        size="sm"
                        onClick={() => onRun(step, 'simulate')}
                        disabled={isRunningStep}
                      >
                        <Play />
                        Simulate signing
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onRun(step, 'real')}
                        disabled={isRunningStep}
                      >
                        I signed it for real
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => onRun(step)}
                      disabled={isRunningStep}
                    >
                      <Play />
                      {isRunningStep ? 'Running…' : 'Run'}
                    </Button>
                  )}
                </div>
              ) : (
                <Badge variant="outline">Waiting</Badge>
              )}
            </div>
            {isNext && (
              <p className="mt-2 text-xs text-muted-foreground">
                Afterwards, check in Twenty: {crmFlowStepChecks[step]}
              </p>
            )}
            {isNext && step === 'sign' && (
              <p className="mt-1 text-xs text-muted-foreground">
                The contract link comes from the Twenty webhook a minute after
                the price step; this page re-reads the deal every few seconds.
                To sign for real, open the deal’s contract link, sign it as the
                clinic, then press “I signed it for real”.
              </p>
            )}
            {isNext && error && (
              <p role="alert" className="mt-2 text-xs text-destructive">
                {error}
              </p>
            )}
            {record && (
              <div className="mt-3">
                <DoneStep record={record} />
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

function CleanUp({ run }: { run: CrmFlowRun }) {
  const { cleanUp, isCleaningUp } = useCleanUpCrmFlow();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (run.cleanedUp)
    return (
      <section aria-label="Clean-up" className="flex flex-col gap-2 text-sm">
        <span className="font-medium">Cleaned up {time(run.cleanedUp.at)}</span>
        <ul className="flex flex-col gap-1 text-xs">
          {Object.entries(run.cleanedUp.steps).map(([part, outcome]) => (
            <li key={part}>
              <span className="font-mono">{part}</span>: {outcome}
            </li>
          ))}
        </ul>
      </section>
    );

  const onConfirm = async () => {
    setError(null);
    try {
      await cleanUp(run.runId);
    } catch (e) {
      setError(failure(e));
    }
  };

  return (
    <div className="flex flex-col items-start gap-2">
      <Button
        variant="destructive"
        onClick={() => setConfirming(true)}
        disabled={isCleaningUp}
      >
        <Trash2 />
        {isCleaningUp ? 'Cleaning up…' : 'Clean up this run'}
      </Button>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      <ConfirmDeleteDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`Clean up run ${run.runId}?`}
        description="Archives the DocuSeal contract, turns off the Stripe payment link and deletes the test Stripe customer, deletes the test Borrah account, and sends the deal, clinic, contact and their notes and tasks to Twenty’s deleted records. Only records made by this run are touched."
        confirmLabel="Clean up"
        isPending={isCleaningUp}
        onConfirm={onConfirm}
      />
    </div>
  );
}

function OpenRun({ runId, onClose }: { runId: string; onClose: () => void }) {
  const { data: run, isLoading, error } = useCrmFlowRun(runId);
  if (isLoading)
    return <p className="text-sm text-muted-foreground">Loading run…</p>;
  if (error || !run)
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not read run {runId}</AlertTitle>
        <AlertDescription>{failure(error)}</AlertDescription>
      </Alert>
    );
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm">
          Run <span className="font-mono">{run.runId}</span>, assigned to{' '}
          <span className="font-medium">{run.testerEmail}</span> · started{' '}
          {time(run.startedAt)} by {run.startedBy} · {run.done.length}/
          {crmFlowStepValues.length} steps
        </p>
        <Button variant="ghost" size="sm" onClick={onClose}>
          <RotateCcw />
          Back to runs
        </Button>
      </div>
      <RecordLinks links={run.records} />
      {run.sinceLastStep.length > 0 && (
        <Alert>
          <AlertTitle>Changed in Twenty since the last step</AlertTitle>
          <AlertDescription>
            <ChangesTable
              changes={run.sinceLastStep}
              label="Changes since the last step"
            />
          </AlertDescription>
        </Alert>
      )}
      <StepList run={run} />
      {!run.next && !run.cleanedUp && (
        <p className="text-sm">
          Every step has run. Check Twenty, then clean up.
        </p>
      )}
      <CleanUp run={run} />
    </div>
  );
}

function StartRun({ onOpen }: { onOpen: (runId: string) => void }) {
  const id = useId();
  const { data: session } = useSession();
  const [typed, setTyped] = useState<string | null>(null);
  const email = typed ?? session?.user?.email ?? '';
  const { start, isStarting } = useStartCrmFlow();
  const [error, setError] = useState<string | null>(null);
  const { data: runs } = useCrmFlowRuns();

  const onStart = async () => {
    setError(null);
    try {
      const run = await start({ email: email.trim() });
      onOpen(run.runId);
    } catch (e) {
      setError(failure(e));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-2">
        <div className="grid gap-1.5">
          <Label htmlFor={`${id}-email`}>Assign the test clinic to</Label>
          <Input
            id={`${id}-email`}
            type="email"
            className="w-72"
            value={email}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="you@borradh.io"
          />
        </div>
        <Button onClick={onStart} disabled={isStarting || !email.trim()}>
          <Play />
          {isStarting ? 'Starting…' : 'Start a run'}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        The deal, the clinic and every task are assigned to this person in
        Twenty, and every Slack message the CRM sends goes to them, labelled
        with who it was meant for. It must be a Borrah email with a Twenty
        account. Starting writes nothing; the first step does.
      </p>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      {runs && runs.length > 0 && (
        <section aria-label="Recent runs" className="flex flex-col gap-2">
          <span className="text-sm font-medium">Recent runs</span>
          <ul className="flex flex-col gap-1 text-sm">
            {runs.map((r) => (
              <li key={r.runId} className="flex items-center gap-2">
                <span className="font-mono">{r.runId}</span>
                <span className="text-muted-foreground">
                  {r.testerEmail} · {r.done}/{crmFlowStepValues.length} steps
                </span>
                {r.cleanedUp && <Badge variant="outline">Cleaned up</Badge>}
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => onOpen(r.runId)}
                  aria-label={`Open run ${r.runId}`}
                >
                  Open
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * The handoff flow test: one made-up clinic walked through the real CRM, a
 * step per button, so each step can be checked in Twenty before the next.
 */
export function HandoffFlowCard() {
  const [runId, setRunId] = useState<string | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Handoff flow test</CardTitle>
        <CardDescription>
          Walks a made-up clinic named “E2E flow …” through the whole handoff,
          from the demo booking to churn, one step per button. After each step,
          open the links and check Twenty. The steps run the real CRM code on
          this host: the Stripe link is real but never paid, the DocuSeal
          contract is real, and every message goes to the person below. The
          scheduled jobs leave these records alone. Clean up when done.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {runId ? (
          <OpenRun runId={runId} onClose={() => setRunId(null)} />
        ) : (
          <StartRun onOpen={setRunId} />
        )}
      </CardContent>
    </Card>
  );
}
